/**
 * Iter 64 (extended Iter 70) — Per-use-case email config.
 *
 * Each system mail uses a specific use-case (HR policy, payroll, PO,
 * client invoice, etc.). This page lets admins set:
 *   • From-Name and From-Email per use-case (always shown)
 *   • SMTP override (host/port/user/password/TLS) per use-case (collapsible).
 *     When SMTP override is set, mails for that use-case go via that mailbox.
 *     When blank, mails fall back to the global SMTP from "Email Configuration".
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Save, RotateCcw, Loader2, ChevronDown, ChevronRight, Server } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';

const USE_CASE_LABEL = {
  hr_policy:      'HR Policy / Handbook',
  hr_digest:      'HR Daily Digest',
  payroll:        'Payroll / Payslips',
  onboarding:     'Onboarding Sequence',
  procurement:    'Procurement (PO / RFQ)',
  finance:        'Finance (Invoices / Payments)',
  projects:       'Project Notifications (DPR / Tasks)',
  tendering:      'Tendering (Bid alerts)',
  client_billing: 'Client Billing (RA Bills)',
  support:        'Support / System Alerts',
  marketing:      'Marketing / Newsletters',
  default:        'Default (fallback)',
};

export default function EmailFromMap() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSmtp, setOpenSmtp] = useState({});  // { use_case: true } if SMTP panel expanded

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/settings/email-from-map');
      setData(r || {});
    } catch (e) { toast.error('Failed to load'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = (useCase, field, value) => {
    setData((d) => ({ ...d, [useCase]: { ...(d[useCase] || {}), [field]: value } }));
  };

  const save = async () => {
    for (const [uc, cfg] of Object.entries(data)) {
      if (cfg?.from_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cfg.from_email)) {
        toast.error(`Invalid email for ${USE_CASE_LABEL[uc] || uc}: ${cfg.from_email}`);
        return;
      }
    }
    setSaving(true);
    try {
      await apiPut('/settings/email-from-map', data);
      toast.success('Email config saved');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="p-10 text-center"><Loader2 size={20} className="animate-spin inline mr-2" />Loading…</div>;
  }

  const ORDERED = ['hr_policy', 'hr_digest', 'payroll', 'onboarding', 'procurement', 'finance', 'projects', 'tendering', 'client_billing', 'support', 'marketing', 'default'];
  const keys = [...ORDERED.filter((k) => k in data), ...Object.keys(data).filter((k) => !ORDERED.includes(k))];

  const hasOverride = (cfg) => !!(cfg?.smtp_host || cfg?.smtp_user || cfg?.smtp_password);

  return (
    <div className="p-6 md:p-8" data-testid="email-from-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader
        overline="Settings · Email"
        title="From-Addresses & Per-Use-Case SMTP"
        subtitle="Route each system mail through the correct mailbox — HR policies from HR, POs from Procurement, payslips from Payroll. Each use-case can also use its own SMTP profile."
        icon={Mail}
      />

      <div className="bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 mt-6 text-xs text-slate-700">
        <strong>How it works:</strong> When a use-case has no SMTP override (collapsed), mail uses the global SMTP profile from
        {' '}<button className="text-[#3b4872] font-bold underline" onClick={() => navigate('/app/settings/email')}>Email Configuration</button>.
        Click <Server size={11} className="inline -mt-0.5" /> to set a dedicated mailbox for that use-case.
      </div>

      <div className="bg-white border border-slate-200 rounded-sm mt-4 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
            <tr>
              <th className="text-left py-2.5 px-3 w-72">Use Case</th>
              <th className="text-left py-2.5 px-3">From Name</th>
              <th className="text-left py-2.5 px-3">From Email</th>
              <th className="text-center py-2.5 px-3 w-32">SMTP Override</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((uc) => {
              const cfg = data[uc] || {};
              const isOpen = openSmtp[uc];
              const overridden = hasOverride(cfg);
              return (
                <React.Fragment key={uc}>
                  <tr className="border-t border-slate-100" data-testid={`row-${uc}`}>
                    <td className="py-2 px-3">
                      <div className="font-display font-bold text-sm">{USE_CASE_LABEL[uc] || uc}</div>
                      <div className="text-[10px] font-mono text-slate-400">{uc}</div>
                      {cfg.description && <div className="text-[10px] text-slate-500 mt-0.5">{cfg.description}</div>}
                    </td>
                    <td className="py-2 px-3">
                      <input type="text" value={cfg.from_name || ''} onChange={(e) => update(uc, 'from_name', e.target.value)} placeholder="Ellora HR"
                        className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#3b4872]"
                        data-testid={`from-name-${uc}`} />
                    </td>
                    <td className="py-2 px-3">
                      <input type="email" value={cfg.from_email || ''} onChange={(e) => update(uc, 'from_email', e.target.value)} placeholder="hr@elloraepc.com"
                        className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#3b4872] font-mono"
                        data-testid={`from-email-${uc}`} />
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => setOpenSmtp((m) => ({ ...m, [uc]: !m[uc] }))}
                        className={`inline-flex items-center gap-1 text-[10px] font-display font-bold uppercase tracking-wider px-2 py-1 rounded ${overridden ? 'bg-emerald-50 text-emerald-800 border border-emerald-300' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}
                        data-testid={`smtp-toggle-${uc}`}
                      >
                        {isOpen ? <ChevronDown size={11}/> : <ChevronRight size={11}/>}
                        {overridden ? 'Custom' : 'Default'}
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/50" data-testid={`smtp-panel-${uc}`}>
                      <td colSpan={4} className="px-3 py-3">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                          <div className="md:col-span-5">
                            <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">SMTP Host</label>
                            <input value={cfg.smtp_host || ''} onChange={(e) => update(uc, 'smtp_host', e.target.value)}
                              placeholder="smtp.gmail.com / smtp.zoho.in / smtp.office365.com"
                              className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:border-[#3b4872] focus:outline-none font-mono"
                              data-testid={`smtp-host-${uc}`} />
                          </div>
                          <div className="md:col-span-2">
                            <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Port</label>
                            <input type="number" value={cfg.smtp_port || ''} onChange={(e) => update(uc, 'smtp_port', e.target.value ? Number(e.target.value) : '')}
                              placeholder="587"
                              className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:border-[#3b4872] focus:outline-none font-mono"
                              data-testid={`smtp-port-${uc}`} />
                          </div>
                          <div className="md:col-span-3">
                            <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">SMTP User</label>
                            <input value={cfg.smtp_user || ''} onChange={(e) => update(uc, 'smtp_user', e.target.value)}
                              placeholder="hr@elloraepc.com"
                              className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:border-[#3b4872] focus:outline-none font-mono"
                              data-testid={`smtp-user-${uc}`} />
                          </div>
                          <div className="md:col-span-2 flex items-end pb-1">
                            <label className="text-xs flex items-center gap-1.5">
                              <input type="checkbox" checked={cfg.use_tls ?? true} onChange={(e) => update(uc, 'use_tls', e.target.checked)}
                                className="w-3.5 h-3.5 accent-[#3b4872]" data-testid={`smtp-tls-${uc}`} />
                              Use TLS
                            </label>
                          </div>
                          <div className="md:col-span-6">
                            <label className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500 block mb-1">Password / App Password</label>
                            <input type="password" value={cfg.smtp_password || ''} onChange={(e) => update(uc, 'smtp_password', e.target.value)}
                              placeholder="Leave blank to keep existing"
                              className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm focus:border-[#3b4872] focus:outline-none font-mono"
                              data-testid={`smtp-password-${uc}`} />
                          </div>
                          <div className="md:col-span-6 text-[11px] text-slate-500 leading-snug pt-1">
                            <strong>Tip:</strong> For Gmail / Google Workspace use an App Password (Account → Security → 2-Step Verification → App passwords). Leave all SMTP fields blank to inherit the global SMTP.
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={load} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider border border-slate-300 rounded-sm hover:bg-slate-50 flex items-center gap-1.5" data-testid="reset-btn">
          <RotateCcw size={11} /> Discard Changes
        </button>
        <button onClick={save} disabled={saving} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] disabled:opacity-50 flex items-center gap-1.5" data-testid="save-btn">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>
    </div>
  );
}
